import React from "react";
import { CheckIcon, DiscussIcon } from "../StrategyCanvasHelpers";
import { ITEM_KIND } from "../StrategyCanvasSummary";
import "./SummaryPanel.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Presentational shell shared by Review Mode's "Review Summary" and Live
// Review's "Meeting Agenda". Both render the same band > section > item tree
// (see StrategyCanvasSummary.js); the agenda additionally gives every item a
// circle to mark it discussed.
// ─────────────────────────────────────────────────────────────────────────────

const BADGE = {
  [ITEM_KIND.SECTION]: "Section comment",
  [ITEM_KIND.OVERALL]: "Overall assessment",
};

// ── Filter chips ──────────────────────────────────────────────────────────────
export const FilterChips = ({ options, value, onChange, children }) => (
  <div className="sp-chips">
    {options.map((option) => (
      <button
        key={option.key}
        type="button"
        className={`sp-chip${value === option.key ? " sp-chip--on" : ""}`}
        onClick={() => onChange(option.key)}
      >
        {option.label}
        <span className="sp-chip-count">{option.count}</span>
      </button>
    ))}
    {children}
  </div>
);

// ── Item ──────────────────────────────────────────────────────────────────────
// Layout is circle | content column, with the content column owning all
// vertical rhythm - that's what keeps a comment aligned under its own title
// rather than running back to the card edge.
// `selectTitle` lets Execution Blueprint say "blueprint" where Strategy Canvas
// says "canvas" — the default keeps Strategy Canvas exactly as it was.
const SummaryItem = ({
  item,
  onSelect,
  selectTitle = "Show this on the canvas",
}) => {
  const scoped =
    item.kind === ITEM_KIND.SECTION || item.kind === ITEM_KIND.OVERALL;

  const discussed = !!item.discussed;

  const classNames = ["sp-item"];
  if (scoped) classNames.push("sp-item--scoped");
  if (item.kind === ITEM_KIND.OVERALL) classNames.push("sp-item--overall");
  // An item that's been discussed is no longer pending, whatever it started as.
  if (!scoped && !discussed && item.status === "to_discuss") {
    classNames.push("sp-item--discuss");
  }
  if (onSelect) classNames.push("sp-item--clickable");

  // The overall assessment never carries a status pill. It's one remark about
  // the whole canvas, sitting alone in its own group under a badge that already
  // names it - a pill there is decoration, not information.
  //
  // Otherwise "Discussed" comes from the payload and outranks everything else:
  // it's a fact about the conversation, so it shows on blocks and section
  // comments alike. A section comment gets nothing while undiscussed, since
  // it's the coach's remark about a scope rather than a decision on a block,
  // and "To discuss" would label it with a state it never had.
  const pill =
    item.kind === ITEM_KIND.OVERALL ? null : discussed ? (
      <span className="sp-pill sp-pill--ok">
        <CheckIcon size={11} />
        Discussed
      </span>
    ) : scoped || !item.status ? null : (
      <span
        className={`sp-pill ${
          item.status === "accepted" ? "sp-pill--ok" : "sp-pill--discuss"
        }`}
      >
        {item.status === "accepted" ? (
          <CheckIcon size={11} />
        ) : (
          <DiscussIcon size={11} />
        )}
        {item.status === "accepted" ? "Accepted" : "To discuss"}
      </span>
    );

  // A real <button> when it's actionable, a plain div otherwise - Review Mode
  // passes no handler, and a button that does nothing is a worse lie than a
  // div. Keyboard and screen-reader behaviour come free this way.
  const Row = onSelect ? "button" : "div";
  const rowProps = onSelect
    ? {
        type: "button",
        onClick: () => onSelect(item),
        title: selectTitle,
      }
    : {};

  return (
    <div className={classNames.join(" ")}>
      {/* No mark-as-discussed control: the agenda is something the coach reads
          from during the session, not a checklist. What settles the canvas is
          finalising each block on the left. */}
      <Row className="sp-item-row" {...rowProps}>
        <div className="sp-item-main">
          <div className="sp-item-top">
            {/* A section or overall comment doesn't get a title: the badge
                already names what it is, and the section heading directly
                above already names which section - printing it again just
                repeats the same words twice in a row. Only a block, whose
                name isn't stated anywhere else, needs one. */}
            <div className="sp-item-titles">
              {scoped ? (
                <span className="sp-badge">
                  {/* An item may name itself (Quarterly OKRs: "Person
                      comment"); otherwise the kind's own label. */}
                  {item.badge || BADGE[item.kind]}
                </span>
              ) : (
                <span className="sp-item-title">{item.title}</span>
              )}
            </div>
            {pill && <span className="sp-item-right">{pill}</span>}
          </div>

          {/* No comment on this item - the row stays a single compact line
              rather than padding itself out with placeholder copy. */}
          {item.comments.length > 0 && (
            <div className="sp-comments">
              {item.comments.map((comment, index) => (
                <div
                  className="sp-comment"
                  key={comment.coach_comment_id ?? `${item.id}-c${index}`}
                >
                  <p>{comment.comment_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </Row>
    </div>
  );
};

// ── Panel body ────────────────────────────────────────────────────────────────
// `renderCount` lets a screen override the right-hand chip on a section
// heading; both currently take the default item count.
const SummaryGroups = ({
  groups,
  renderCount,
  onSelectItem,
  selectTitle,
  emptyLabel = "Nothing to show here.",
}) => {
  if (!groups.length) return <div className="sp-empty">{emptyLabel}</div>;

  return (
    <>
      {groups.map((group, groupIndex) => (
        <React.Fragment key={group.key}>
          {/* Hairline between sections only - never above the first. */}
          {groupIndex > 0 && <div className="sp-separator" />}

          <div className="sp-section-head">
            <span
              className={`sp-section-rail sp-section-rail--${group.color}`}
            />
            <span className="sp-section-titles">
              {group.band && (
                <span className="sp-section-band">{group.band}</span>
              )}
              <span className="sp-section-name">{group.name}</span>
            </span>
            {renderCount ? (
              renderCount(group)
            ) : (
              <span className="sp-section-count">{group.items.length}</span>
            )}
          </div>

          <div className="sp-items">
            {group.items.map((item) => (
              <SummaryItem
                key={item.id}
                item={item}
                selectTitle={selectTitle}
                onSelect={
                  onSelectItem ? () => onSelectItem(item, group) : undefined
                }
              />
            ))}
          </div>
        </React.Fragment>
      ))}
    </>
  );
};

export default SummaryGroups;
